/**
 * Главный экран владельца: матрица «объект × уровень защиты».
 *
 * Именно матрица, а не список копий. Список отвечает на вопрос «что
 * забэкапилось», а нужен ответ на вопрос «что у нас есть и как оно защищено».
 * Машина, которой нет ни в одной копии, в списке копий не появится вовсе —
 * а на этом экране она обязана быть строкой с пустыми клетками.
 */
import { useState } from 'react';
import { useQuery } from '../transport/hooks.ts';
import { StatusBadge } from '../components/StatusBadge.tsx';
import {
  countsAsProblem, LEVELS, LEVEL_HINTS, LEVEL_LABELS, LEVEL_SHORT,
} from '../../shared/domain/status.ts';
import type { LevelName, LevelPolicy, Verdict } from '../../shared/domain/status.ts';
import type {
  OverviewCell,
  OverviewPlatform,
  OverviewSnapshot,
  OverviewSource,
  PlatformGroup,
} from '../../shared/protocol/overview.ts';
import { Segmented } from '../components/Segmented.tsx';
import { BrandIcon } from '../components/BrandIcon.tsx';
import { Hint } from '../components/Hint.tsx';
import { ago, agoPhrase } from '../lib/format.ts';
import { EditName } from '../components/EditName.tsx';
import { request } from '../transport/socket.ts';
import { useParam } from '../router.tsx';

const KIND_LABELS: Record<OverviewSource['kind'], string> = {
  vm: 'виртуальная машина',
  db: 'база данных',
  site: 'сайт',
  app: 'приложение',
};

/**
 * Значок вида места. Не украшение: облако и свой гипервизор выглядят на
 * матрице одинаковыми строками, а разница между ними в том, кто хозяин
 * железу — и что будет при потере доступа к аккаунту.
 */
const PLACE_GLYPH: Record<string, string> = {
  machine: '▢',
  hypervisor: '▤',
  cloud: '☁',
  location: '⌂',
};

/**
 * Значок канала. Не украшение: он отвечает на вопрос «откуда мы это знаем»,
 * а без ответа пустая клетка читается как «копий нет» — хотя может значить
 * «мы не видим». Это две разные новости, и вторая хуже.
 */
const CHANNEL_GLYPH: Record<string, string> = {
  live: '⇄',
  stale: '⇄',
  failed: '⚠',
  off: '⊘',
  unconfigured: '⊘',
  none: '?',
};

const CHANNEL_WORD: Record<string, string> = {
  live: 'данные идут по API',
  stale: 'канал есть, сборщик молчит',
  failed: 'сбор не удаётся',
  off: 'выключено владельцем',
  unconfigured: 'не настроено',
  none: 'канала нет',
};

/** Худшее состояние из каналов площадки: одна поломка важнее трёх успехов. */
const CHANNEL_ORDER = ['live', 'stale', 'off', 'unconfigured', 'failed'];

function channelState(channels: OverviewPlatform['channels']): string {
  if (channels.length === 0) return 'none';
  return channels
    .map((c) => c.state)
    .reduce((a, b) => (CHANNEL_ORDER.indexOf(b) > CHANNEL_ORDER.indexOf(a) ? b : a));
}

function ChannelHint({ platform }: { platform: OverviewPlatform }): React.JSX.Element {
  if (platform.channels.length === 0) {
    return (
      <div className="hint__body">
        <b className="hint__title">Канала нет</b>
        <p className="hint__text">
          Консоль сюда не ходит. Всё, что показано в строках этой площадки,
          либо унаследовано от родителей, либо введено руками.
        </p>
        <p className="hint__text hint__text--warn">
          Пустая клетка здесь значит «узнать нельзя», а не «копий нет».
        </p>
      </div>
    );
  }

  return (
    <div className="hint__body">
      <b className="hint__title">
        {platform.channels.length === 1 ? 'Канал данных' : 'Каналы данных'}
      </b>
      {platform.channels.map((c) => (
        <div key={c.key} className={`hint__ch hint__ch--${c.state}`}>
          <div className="hint__ch-head">
            <span className="hint__ch-name">{c.label}</span>
            <span className="hint__ch-state">{CHANNEL_WORD[c.state]}</span>
          </div>
          <dl className="hint__ch-rows">
            <div><dt>куда</dt><dd className="mono">{c.endpoint}</dd></div>
            <div><dt>чем</dt><dd>{c.access}</dd></div>
            <div><dt>даёт</dt><dd>{c.provides}</dd></div>
            <div>
              <dt>сбор</dt>
              <dd>
                {agoPhrase(c.lastCollectAt)}
                {/*
                  Доступность и доступ — разные факты. Открытый порт не значит,
                  что нас пустят; успешный сбор значит, что права работали.
                */}
                {/*
                  Агент подключается сам, а к остальным ходим мы — и слова
                  для «нет связи» у них разные. «Адрес молчит» про агента
                  сказать нельзя: у него нет адреса, по которому его зовут.
                */}
                {c.reachable === false && (
                  <span className="hint__no">
                    {c.key.startsWith('agent:') ? ' · сейчас не на связи' : ' · адрес молчит'}
                  </span>
                )}
              </dd>
            </div>
          </dl>
        </div>
      ))}
    </div>
  );
}

const VERDICT_SUMMARY: { verdict: Verdict; label: string }[] = [
  { verdict: 'missing', label: 'без копий' },
  { verdict: 'failed', label: 'сбой' },
  { verdict: 'stale', label: 'устарели' },
  { verdict: 'warn', label: 'внимание' },
  { verdict: 'unknown', label: 'нет данных' },
  { verdict: 'ok', label: 'в норме' },
];

/** Свидетели разошлись — это находка сама по себе, а не техническая деталь. */
function disagree(cell: OverviewCell): boolean {
  return !!cell.witnesses && new Set(cell.witnesses.map((w) => w.verdict)).size > 1;
}

function Cell({
  cell,
  policy,
  observable,
}: {
  cell: OverviewCell | undefined;
  policy: LevelPolicy | undefined;
  observable: boolean;
}): React.JSX.Element {
  // Пустая клетка сама по себе ничего не значит. Смысл ей придаёт политика:
  // при not-needed это норма, при undecided — вопрос без ответа, при
  // expected — либо дыра, либо слепое пятно. Свести их в одно изображение
  // значит либо кричать, где решения нет, либо молчать, где дыра.
  // Решение «уровень не нужен» сильнее любого наблюдения: сборщик всё равно
  // отметит отсутствие копий, но спорить с принятым решением экран не должен.
  // Само наблюдение при этом не теряется — оно в подсказке.
  if (policy === 'not-needed') {
    return (
      <td className="matrix__cell matrix__cell--na">
        <span title={cell ? `Уровень не нужен. Наблюдалось: ${cell.reason}` : 'Уровень не нужен'}>·</span>
      </td>
    );
  }

  if (!cell) {
    if (policy === 'expected') {
      return observable ? (
        <td className="matrix__cell matrix__cell--missing">
          <StatusBadge status="missing" />
        </td>
      ) : (
        <td className="matrix__cell matrix__cell--blind" title="Сборщик для этой площадки не настроен — узнать нельзя">
          <StatusBadge status="unknown" />
          <span className="matrix__age">не видим</span>
        </td>
      );
    }
    if (policy === 'undecided') {
      return (
        <td className="matrix__cell matrix__cell--undecided" title="Не решено, нужен ли здесь бэкап">
          <span className="matrix__undecided">не решено</span>
        </td>
      );
    }
    return <td className="matrix__cell matrix__cell--na"><span title="Уровень не нужен">·</span></td>;
  }

  return (
    <td className={`matrix__cell matrix__cell--${cell.verdict}`}>
      <span className="matrix__mark" title={cell.reason}>
        <StatusBadge status={cell.verdict} />
      </span>
      {cell.inheritedFrom && (
        <span className="matrix__inherited" title={`Защищено образом «${cell.inheritedFrom}» целиком`}>
          ↑ {cell.inheritedFrom}
        </span>
      )}
      <span className="matrix__age">{ago(cell.lastAt)}</span>
      {disagree(cell) && (
        <span className="matrix__split" title="Свидетели расходятся — раскройте строку">
          расхождение
        </span>
      )}
    </td>
  );
}

function SourceRow({ source, onRenamed }: {
  source: OverviewSource;
  onRenamed: () => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const hasDetail = Object.keys(source.cells).length > 0;

  const rename = async (key: string, label: string, note: string): Promise<void> => {
    setSaving(true);
    try {
      await request('inventory.bind', { key, label, note });
      onRenamed();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <tr
        className={`matrix__row matrix__row--${source.verdict}${open ? ' matrix__row--open' : ''}${source.lifecycle ? ' matrix__row--dim' : ''}`}
        onClick={() => hasDetail && setOpen((v) => !v)}
      >
        {/*
          Правка на строке останавливает раскрытие: щелчок по полю ввода не
          должен схлопывать строку, в которой это поле лежит.
        */}
        <th scope="row" className="matrix__name" onClick={(e) => e.stopPropagation()}>
          <EditName
            objectKey={source.key}
            shown={source.name}
            {...(source.factName ? { factName: source.factName } : {})}
            {...(source.note ? { note: source.note } : {})}
            busy={saving}
            onSave={rename}
          />
          {/*
            Где объект физически. Без этого строки «as2» и «bitrix-old»
            выглядят одинаковыми, а восстановление у них разное: одно —
            контейнер внутри виртуалки, другое — сама виртуалка. Вспоминать
            это приходится в неудачный момент.
          */}
          {source.place.length > 0 && (
            <span className="place">
              {source.place.map((p) => (
                <span key={p.label} className={`place__item place__item--${p.kind}`} title={p.title}>
                  <span className="place__glyph" aria-hidden="true">{PLACE_GLYPH[p.kind]}</span>
                  {p.label}
                </span>
              ))}
            </span>
          )}
          <span className="matrix__kind">
            {KIND_LABELS[source.kind]}
            {source.lifecycle === 'retired' && (
              <span className="matrix__lifecycle" title="Владелец объявил объект неактивным. В счёт проблем не идёт, но с экрана не убран: пока диск существует, данные на нём лежат.">
                выведено из эксплуатации
              </span>
            )}
            {source.lifecycle === 'planned' && (
              <span className="matrix__lifecycle" title="Объекта ещё нет">планируется</span>
            )}
          </span>
        </th>
        {LEVELS.map((level) => (
          <Cell
            key={level}
            cell={source.cells[level]}
            policy={source.levels[level]}
            observable={source.observable}
          />
        ))}
      </tr>

      {open && (
        <tr className="matrix__detail">
          <td colSpan={LEVELS.length + 1}>
            <dl className="detail">
              {(Object.entries(source.cells) as [LevelName, OverviewCell][]).map(([level, cell]) => (
                <div key={level} className="detail__level">
                  <dt>{LEVEL_LABELS[level]}</dt>
                  <dd>
                    <p className="detail__reason">{cell.reason}</p>
                    {cell.witnesses && cell.witnesses.length > 1 && (
                      <ul className="detail__witnesses">
                        {cell.witnesses.map((w) => (
                          <li key={w.system}>
                            <span className="detail__system">{w.system}</span>
                            <StatusBadge status={w.verdict} />
                            <span className="detail__wreason">{w.reason}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </dd>
                </div>
              ))}
              {Object.keys(source.observed).length > 0 && (
                <div className="detail__level">
                  <dt>Наблюдаемое</dt>
                  <dd className="mono detail__observed">
                    {Object.entries(source.observed)
                      .map(([k, v]) => `${k}=${String(v)}`)
                      .join('  ')}
                  </dd>
                </div>
              )}
            </dl>
          </td>
        </tr>
      )}
    </>
  );
}

function PlatformTable({
  platform, onRenamed,
}: {
  platform: OverviewSnapshot['platforms'][number];
  onRenamed: () => void;
}): React.JSX.Element {
  return (
    <table className="table matrix__table">
      <thead>
        <tr>
          <th scope="col">Объект</th>
          {LEVELS.map((level) => (
            <th key={level} scope="col" className="matrix__head">
              {/*
                Короткая подпись экономит место, но «ФАЙЛЫ» и «ДАННЫЕ» рядом
                одинаково загадочны, а разница между ними решающая. Полное имя
                и объяснение — в подсказке, чтобы не гадать по трём буквам.
              */}
              <Hint content={
                <div className="hint__body">
                  <b className="hint__title">{LEVEL_LABELS[level]}</b>
                  <p className="hint__text">{LEVEL_HINTS[level]}</p>
                </div>
              }>
                {LEVEL_SHORT[level]}
              </Hint>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {platform.sources.map((source) => (
          <SourceRow key={source.key} source={source} onRenamed={onRenamed} />
        ))}
      </tbody>
    </table>
  );
}

/**
 * Площадки, сгруппированные по природе защиты.
 *
 * Порядок групп не алфавитный: сверху то, чем защищено больше всего машин, и
 * то, о чём владельца спрашивают первым. «Прочее» последнее — там площадки,
 * про которые ещё не решили, куда они относятся, и это честнее, чем
 * приписать их к соседней группе по догадке.
 */
const GROUP_ORDER: PlatformGroup[] = [
  'hypervisor', 'containers', 'cloud', 'database', 'network', 'other',
];

const GROUP_LABELS: Record<PlatformGroup, string> = {
  hypervisor: 'Гипервизоры',
  containers: 'Контейнеризация',
  cloud: 'Облако',
  database: 'Базы данных',
  network: 'Сеть и устройства',
  other: 'Прочее',
};

export function Overview(): React.JSX.Element {
  const { data, error, loading, reload } = useQuery<OverviewSnapshot>('overview.get');
  // Пустая строка значит «ещё не выбирали»: подставлять первое каждый раз
  // нельзя — обновление снимка сбрасывало бы выбранное под руками.
  //
  // Выбор живёт в адресе: «посмотри вот эту матрицу» должно работать ссылкой,
  // а обновление страницы — возвращать на ту же группу, а не в начало.
  const [group, setGroup] = useParam('group', '');
  const [platformKey, setPlatformKey] = useParam('platform', '');

  if (error) {
    return (
      <section className="panel panel--fill">
        <h2 className="panel__title">Общая картина</h2>
        <p className="alert alert--error">Ошибка: {error.msg}</p>
      </section>
    );
  }

  if (!data) {
    return (
      <section className="panel panel--fill">
        <h2 className="panel__title">Общая картина</h2>
        <p className="muted">{loading ? 'Собираем картину…' : 'Нет данных.'}</p>
      </section>
    );
  }

  const silent = data.collectors.filter((c) => c.enabled && c.ok !== true);

  // Правило одно на всё приложение: см. countsAsProblem. Здесь оно было своё
  // и исключало unknown, из-за чего число в письме не сходилось с экраном.
  const problemsIn = (p: OverviewSnapshot['platforms'][number]): number =>
    p.sources.filter((s) => countsAsProblem(s.verdict, s.lifecycle)).length;

  // Пустые группы не показываем вовсе. Сегмент «Облако — 0» не сообщает
  // ничего, кроме того, что мы завели такое слово.
  const groups = GROUP_ORDER
    .map((g) => {
      const platforms = data.platforms.filter((p) => p.group === g);
      return {
        id: g,
        label: GROUP_LABELS[g],
        platforms,
        count: platforms.reduce((n, p) => n + p.sources.length, 0),
        problems: platforms.reduce((n, p) => n + problemsIn(p), 0),
        blind: platforms.length > 0 && platforms.every((p) => p.sources.every((s) => !s.observable)),
      };
    })
    .filter((g) => g.platforms.length > 0);

  const activeGroup = groups.find((g) => g.id === group) ?? groups[0];
  const platforms = activeGroup?.platforms ?? [];
  const platform = platforms.find((p) => p.key === platformKey) ?? platforms[0];

  return (
    <section className="panel panel--fill">
      <header className="panel__head">
        <h2 className="panel__title">Общая картина</h2>

        <ul className="summary">
          {VERDICT_SUMMARY.filter((s) => data.totals.byVerdict[s.verdict] > 0).map((s) => (
            <li key={s.verdict} className={`summary__item summary__item--${s.verdict}`}>
              <span className="summary__count">{data.totals.byVerdict[s.verdict]}</span>
              <span className="summary__label">{s.label}</span>
            </li>
          ))}
          {data.totals.undecided > 0 && (
            <li className="summary__item summary__item--undecided">
              <span className="summary__count">{data.totals.undecided}</span>
              <span className="summary__label">не решено, нужен ли бэкап</span>
            </li>
          )}
        </ul>

        {/*
          Молчащий сборщик — не мелочь. Пока он молчит, экран показывает
          вчерашнюю правду с сегодняшней уверенностью, и это опаснее видимой
          ошибки. Потому предупреждение стоит в шапке и не уезжает при
          прокрутке таблицы.
        */}
        {silent.length > 0 && (
          <p className="alert alert--error">
            Не отработали: {silent.map((c) => c.label).join(', ')}. Пока сборщик молчит,
            данные ниже устаревают, оставаясь на вид свежими.
          </p>
        )}

        <div className="switchers">
          <Segmented
            ariaLabel="Вид защиты"
            items={groups.map((g) => ({
              id: g.id,
              label: g.label,
              count: g.count,
              alert: g.problems > 0,
              blind: g.blind,
            }))}
            active={activeGroup?.id ?? ''}
            onSelect={(id) => { setGroup(id); setPlatformKey(''); }}
          />

          {/*
            Второй ряд только когда площадок больше одной. Переключатель из
            одного сегмента — это не выбор, а украшение, которое занимает
            строку и учит нажимать на неработающее.
          */}
          {platforms.length > 1 && (
            <Segmented
              size="sm"
              ariaLabel="Площадка"
              items={platforms.map((p) => {
                // Площадка, где всё запланировано, — это задача, а не
                // хозяйство. Показываем приглушённо: убрать значит забыть,
                // показать наравне — соврать, что там что-то работает.
                const planned = p.sources.length > 0
                  && p.sources.every((s) => s.lifecycle === 'planned');
                const state = channelState(p.channels);
                return {
                  id: p.key,
                  label: p.label,
                  count: p.sources.length,
                  alert: problemsIn(p) > 0,
                  muted: planned,
                  ...(p.brand ? { icon: <BrandIcon brand={p.brand} size={15} /> } : {}),
                  // Знак вопроса заменён значком канала: он говорит то же
                  // самое и ещё три вещи сверх.
                  channel: {
                    glyph: CHANNEL_GLYPH[state] ?? '?',
                    state,
                    hint: <ChannelHint platform={p} />,
                  },
                  ...(planned ? { title: 'Площадка объявлена, но ничего ещё не развёрнуто' } : {}),
                };
              })}
              active={platform?.key ?? ''}
              onSelect={setPlatformKey}
            />
          )}
        </div>
      </header>

      {/* Прокручивается только это. Шапка и подвал остаются на виду. */}
      <div className="panel__scroll">
        {platform
          ? <PlatformTable platform={platform} onRenamed={reload} />
          : <p className="muted">В этой группе площадок нет.</p>}
      </div>

      <footer className="panel__foot">
        <span className="legend__item"><b>↑</b> защита от образа машины</span>
        <span className="legend__item"><b>не решено</b> уровень не объявлен</span>
        <span className="legend__item"><b>не видим</b> сборщика нет</span>
        <span className="legend__item"><b>·</b> уровень не нужен</span>
        <span className="panel__stamp">
          снимок {new Date(data.generatedAt).toLocaleString('ru-RU')}
        </span>
      </footer>
    </section>
  );
}
